import { LoadingButton } from '@mui/lab';
import { Alert, Button, Collapse, Divider, Grid, TextField } from '@mui/material';
import { FormikValues } from 'formik';
import { get, isString } from 'lodash';
import { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import ReactInputMask from 'react-input-mask';
import { useLanguageTabs } from 'src/hooks/useLanguageTabs';
import { useAuth, useEdit } from '../../../hooks';
import {
  clearLocation,
  clearLocationRequest,
  createLocation,
  editLocation,
  forceGetLocationWarehouse,
  getLocation,
} from '../../../thunks/location-thunks';
import { DirectionsSelect } from '../../directions';
import { DistrictsSelect } from '../../districts';
import { OrganizationsSelect } from '../../organizations';
import { RegionsSelect } from '../../regions';
import {
  FIELD_ADDRESS,
  FIELD_ADDRESS_OZ,
  FIELD_ADDRESS_RU,
  FIELD_ADDRESS_UZ,
  FIELD_DIRECTION,
  FIELD_DISTRICT,
  FIELD_EMAIL,
  FIELD_NAME,
  FIELD_NAME_OZ,
  FIELD_NAME_RU,
  FIELD_NAME_UZ,
  FIELD_ORGANIZATION,
  FIELD_PHONE,
  FIELD_PLACE_COUNT,
  FIELD_REGION,
  LocationsEditValidationForm,
} from './locations-edit-schema';

export interface LocationsEditFormProps {
  id?: string | number;
  onCancel?: () => void;
  onSubmit: (values: FormikValues) => void;
}

export const LocationsEditForm: ComponentType<LocationsEditFormProps> = ({
  id,
  onCancel,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { tabLang, renderTabsButtons } = useLanguageTabs();
  const { user, isSuperAdmin } = useAuth();
  const { isLoading, isFetching, error, formik, handleFieldChange, hasError, getErrorMessage } =
    useEdit({
      id,
      entity: 'location',
      initialValues: {
        [FIELD_ORGANIZATION]: get(user, ['data', 'organization', 'id'], ''),
        [FIELD_DIRECTION]: '',
        [FIELD_REGION]: '',
        [FIELD_DISTRICT]: '',
        [FIELD_ADDRESS]: {
          ru: '',
          uz: '',
          oz: '',
        },
        [FIELD_PHONE]: '',
        [FIELD_EMAIL]: '',
        [FIELD_PLACE_COUNT]: '',
        [FIELD_NAME]: {
          ru: '',
          uz: '',
          oz: '',
        },
      },
      validationSchema: LocationsEditValidationForm,
      onGetWareHouse: forceGetLocationWarehouse,
      onGetOne: getLocation,
      onClearOne: clearLocation,
      onClear: clearLocationRequest,
      onCreate: createLocation,
      onEdit: editLocation,
      onSubmit: onSubmit,
    });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container mb={3}>
        <Grid item xs={12} mb={2}>
          {renderTabsButtons()}
        </Grid>
        {tabLang === 'ru' && (
          <Grid item xs={12}>
            <TextField
              {...formik.getFieldProps(FIELD_NAME_RU)}
              onChange={(e: any) => handleFieldChange(FIELD_NAME_RU, e)}
              error={hasError(FIELD_NAME_RU)}
              helperText={getErrorMessage(FIELD_NAME_RU)}
              label={t('location.name.label', 'Название') + ' (' + t('russian', 'Русский') + ')'}
              placeholder={t('location.name.placeholder', 'Введите название')}
              disabled={isLoading || isFetching}
              fullWidth
            />
          </Grid>
        )}
        {tabLang === 'uz' && (
          <Grid item xs={12}>
            <TextField
              {...formik.getFieldProps(FIELD_NAME_UZ)}
              onChange={(e: any) => handleFieldChange(FIELD_NAME_UZ, e)}
              error={hasError(FIELD_NAME_UZ)}
              helperText={getErrorMessage(FIELD_NAME_UZ)}
              label={t('location.name.label', 'Название') + ' (' + t('uzbek', 'Узбекский') + ')'}
              placeholder={t('location.name.placeholder', 'Введите название')}
              disabled={isLoading || isFetching}
              fullWidth
            />
          </Grid>
        )}
        {tabLang === 'oz' && (
          <Grid item xs={12}>
            <TextField
              {...formik.getFieldProps(FIELD_NAME_OZ)}
              onChange={(e: any) => handleFieldChange(FIELD_NAME_OZ, e)}
              error={hasError(FIELD_NAME_OZ)}
              helperText={getErrorMessage(FIELD_NAME_OZ)}
              label={t('location.name.label', 'Название') + ' (' + t('ozbek') + ')'}
              placeholder={t('location.name.placeholder', 'Введите название')}
              disabled={isLoading || isFetching}
              fullWidth
            />
          </Grid>
        )}
        {isSuperAdmin && (
          <Grid item xs={12}>
            <OrganizationsSelect
              {...formik.getFieldProps(FIELD_ORGANIZATION)}
              onChange={(e: any) => handleFieldChange(FIELD_ORGANIZATION, e)}
              error={hasError(FIELD_ORGANIZATION)}
              helperText={getErrorMessage(FIELD_ORGANIZATION)}
              label={t('location.organization.label', 'Организация')}
              placeholder={t('location.organization.placeholder', 'Выберите организацию')}
              disabled={isLoading || isFetching}
              fullWidth
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <DirectionsSelect
            {...formik.getFieldProps(FIELD_DIRECTION)}
            onChange={(e: any) => handleFieldChange(FIELD_DIRECTION, e)}
            error={hasError(FIELD_DIRECTION)}
            helperText={getErrorMessage(FIELD_DIRECTION)}
            label={t('direction', 'Направление')}
            placeholder={t('direction', 'Направление')}
            organization={get(formik, ['values', FIELD_ORGANIZATION], null)}
            disabled={isLoading || isFetching}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <RegionsSelect
            {...formik.getFieldProps(FIELD_REGION)}
            onChange={(e: any) => handleFieldChange(FIELD_REGION, e)}
            error={hasError(FIELD_REGION)}
            helperText={getErrorMessage(FIELD_REGION)}
            label={t('location.region.label', 'Регион')}
            // placeholder={t('location.region.placeholder', 'Выберите регион')}
            disabled={isLoading || isFetching}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <DistrictsSelect
            {...formik.getFieldProps(FIELD_DISTRICT)}
            onChange={(e: any) => handleFieldChange(FIELD_DISTRICT, e)}
            error={hasError(FIELD_DISTRICT)}
            helperText={getErrorMessage(FIELD_DISTRICT)}
            label={t('location.district.label', 'Район')}
            // placeholder={t('location.district.placeholder', 'Выберите район')}
            region={get(formik, ['values', FIELD_REGION], null)}
            disabled={isLoading || isFetching}
            fullWidth
          />
        </Grid>
        <Grid container item spacing={3}>
          {tabLang === 'ru' && (
            <Grid item xs={6}>
              <TextField
                {...formik.getFieldProps(FIELD_ADDRESS_RU)}
                onChange={(e: any) => handleFieldChange(FIELD_ADDRESS_RU, e)}
                error={hasError(FIELD_ADDRESS_RU)}
                helperText={getErrorMessage(FIELD_ADDRESS_RU)}
                label={t('location.address.label', 'Адрес') + ' (' + t('russian', 'Русский') + ')'}
                placeholder={t('location.address.placeholder', 'Введите адрес')}
                disabled={isLoading || isFetching}
                fullWidth
              />
            </Grid>
          )}
          {tabLang === 'uz' && (
            <Grid item xs={6}>
              <TextField
                {...formik.getFieldProps(FIELD_ADDRESS_UZ)}
                onChange={(e: any) => handleFieldChange(FIELD_ADDRESS_UZ, e)}
                error={hasError(FIELD_ADDRESS_UZ)}
                helperText={getErrorMessage(FIELD_ADDRESS_UZ)}
                label={t('location.address.label', 'Адрес') + ' (' + t('uzbek') + ')'}
                placeholder={t('location.address.placeholder', 'Введите адрес')}
                disabled={isLoading || isFetching}
                fullWidth
              />
            </Grid>
          )}
          {tabLang === 'oz' && (
            <Grid item xs={6}>
              <TextField
                {...formik.getFieldProps(FIELD_ADDRESS_OZ)}
                onChange={(e: any) => handleFieldChange(FIELD_ADDRESS_OZ, e)}
                error={hasError(FIELD_ADDRESS_OZ)}
                helperText={getErrorMessage(FIELD_ADDRESS_OZ)}
                label={t('location.address.label', 'Адрес') + ' (' + t('ozbek') + ')'}
                placeholder={t('location.address.placeholder', 'Введите адрес')}
                disabled={isLoading || isFetching}
                fullWidth
              />
            </Grid>
          )}
          <Grid item xs={6}>
            <ReactInputMask
              {...formik.getFieldProps(FIELD_PHONE)}
              onChange={(e: any) => handleFieldChange(FIELD_PHONE, e)}
              disabled={isLoading || isFetching}
              mask="+\9\9\8 99 999 99 99"
              maskChar=""
            >
              {() => (
                <TextField
                  fullWidth
                  helperText={getErrorMessage(FIELD_PHONE)}
                  error={hasError(FIELD_PHONE)}
                  label={t('profile.phone', 'Номер телефона')}
                  placeholder={t('profile.phone.placeholder', 'Введите номер телефона')}
                />
              )}
            </ReactInputMask>
          </Grid>
        </Grid>
        <Grid container item spacing={3}>
          <Grid item xs={6}>
            <TextField
              {...formik.getFieldProps(FIELD_EMAIL)}
              onChange={(e: any) => handleFieldChange(FIELD_EMAIL, e)}
              error={hasError(FIELD_EMAIL)}
              helperText={getErrorMessage(FIELD_EMAIL)}
              label={t('location.email.label', 'Электронная почта')}
              placeholder={t('location.email.placeholder', 'Введите электронную почту')}
              disabled={isLoading || isFetching}
              fullWidth
              type="email"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...formik.getFieldProps(FIELD_PLACE_COUNT)}
              onChange={(e: any) => handleFieldChange(FIELD_PLACE_COUNT, e)}
              error={hasError(FIELD_PLACE_COUNT)}
              helperText={getErrorMessage(FIELD_PLACE_COUNT)}
              label={t('location.placeCount.label', 'Количество посадочных мест')}
              placeholder={t('location.placeCount.placeholder', 'Введите количество мест')}
              disabled={isLoading || isFetching}
              type="number"
              InputProps={{ inputProps: { min: 0 } }}
              fullWidth
            />
          </Grid>
        </Grid>
      </Grid>
      {isString(error) && (
        <Collapse in={!!error}>
          <Alert severity="error">{error}</Alert>
        </Collapse>
      )}
      <Divider sx={{ mt: 3, mb: 4 }} />
      <Grid container columnSpacing={2} justifyContent="space-between">
        <Grid item>
          {onCancel && (
            <Button onClick={onCancel} variant="outlined" size="large" disabled={isLoading}>
              {t('cancel', 'Отмена')}
            </Button>
          )}
        </Grid>
        <Grid item>
          <LoadingButton
            loading={isLoading || isFetching}
            type="submit"
            variant="contained"
            color="secondary"
            size="large"
          >
            {t('action.save', 'Сохранить')}
          </LoadingButton>
        </Grid>
      </Grid>
    </form>
  );
};
